import {
  Box,
  Typography,
  FormControl,
  MenuItem,
  Select,
  Button,
  Paper,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { FlexBox } from 'components/Containers/FlexBox';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import { ThemePalette } from 'mui.theme';
import {
  countTiresInDays,
  updateTireWarningCounts,
} from 'views/TirePressure/util';
import dayjs from 'dayjs';
import StyledSwitchButton from 'components/Buttons/SwitchButton';
import useAppTheme from 'hooks/useAppTheme';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

// Custom tooltip component that adapts to theme
const CustomTooltip = ({ active, payload, label }: TooltipProps<any, any>) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  if (!active || !payload || !payload.length) return null;

  const tooltipBgColor = enableDarkTheme
    ? `#172A46bb`
    : `rgba(217, 229, 247, 0.8)`;
  const tooltipBorderColor = palette.borderGrey;
  const tooltipTextColor = palette.textPrimary;
  const tooltipLabelColor = enableDarkTheme
    ? ThemePalette.typography.lightGrey
    : ThemePalette.typography.grey;

  return (
    <Paper
      elevation={3}
      sx={{
        padding: '10px',
        backgroundColor: tooltipBgColor,
        border: `1px solid ${tooltipBorderColor}`,
        borderRadius: '4px',
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Typography
        sx={{
          fontSize: '12px',
          color: tooltipLabelColor,
          marginBottom: '5px',
        }}
      >
        {convertStdDateString(label)}
      </Typography>
      {payload.map((entry: any, index: number) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: index < payload.length - 1 ? 1 : 0,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              backgroundColor: entry.color,
              marginRight: 1,
              borderRadius: '2px',
            }}
          />
          <Typography
            sx={{
              fontSize: '12px',
              color: tooltipTextColor,
              fontWeight: 500,
            }}
          >
            {entry.name || entry.dataKey}: {entry.value}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

const DayWiseTrend = ({
  metricType,
  setMetricType,
  sortBy,
  handleChange,
  selectedDate,
  setSelectedDate,
  data,
  notifications,
  selectedAsset,
}: {
  metricType: string;
  setMetricType: any;
  sortBy: string;
  handleChange: any;
  selectedDate: string | null;
  setSelectedDate: any;
  data: any[];
  notifications: any[];
  selectedAsset: {
    bumperId: string;
    deviceId: string;
  } | null;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const filteredData = selectedAsset
    ? data.filter((item) => item.asset === selectedAsset.deviceId)
    : data;
  const filteredNotifications = selectedAsset
    ? notifications.filter(
        (item) => item.asset.bumperNumber === selectedAsset.bumperId
      )
    : notifications;

  const fontColor = palette.textPrimary;

  const legendItems = [
    {
      label: t('tire_ok_2'),
      color: '#58BF6A',
    },
    {
      label: t('tire_warning_2'),
      color: '#EB5757',
    },
  ];

  let tiresInDays = countTiresInDays(filteredData);

  tiresInDays = tiresInDays.sort(
    (a, b) => dayjs(a.day).valueOf() - dayjs(b.day).valueOf()
  );
  tiresInDays = sortBy ? tiresInDays.slice(-Number(sortBy)) : tiresInDays;

  let chartData = tiresInDays.map((item) => {
    return {
      ...item,
      tireOk: item.total,
      tireWarning: 0,
    };
  });

  chartData = updateTireWarningCounts(chartData, filteredNotifications);
  chartData = chartData.sort((a: any, b: any) => a.day.localeCompare(b.day));

  const handleBarClick = (data: any) => {
    setSelectedDate(data.day);
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMetricType(metricType === 'Count' ? 'Trend' : 'Count');
    setSelectedDate(null);
  };

  const createActiveDot = (dataKey: any) => ({
    r: 6,
    style: { cursor: 'pointer' },
    onClick: (event: any, dotProps: any) => {
      setSelectedDate(dotProps?.payload?.day);
    },
  });

  return (
    <Box
      sx={{
        marginTop: '10px',
        height: '500px',
        borderRadius: '8px',
        border: '1px solid #2F445E',
        padding: '10px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{
              color: fontColor,
              fontSize: '14px',
              fontWeight: '700',
            }}
          >
            {t('day_wise_trend')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              marginTop: '10px',
            }}
          >
            {legendItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: '20px',
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: item.color,
                    borderRadius: 0.5,
                    marginRight: '10px',
                  }}
                />
                <Typography sx={{ color: fontColor, fontSize: '12px' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: fontColor,
            }}
          >
            {t('trend')}
          </Typography>
          <StyledSwitchButton
            checked={metricType === 'Count'}
            onChange={handleSwitchChange}
          />

          <Typography
            sx={{
              fontSize: '12px',
              color: fontColor,
            }}
          >
            {t('count')}
          </Typography>
          {selectedDate ? (
            <>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: fontColor,
                  marginLeft: '10px',
                  marginRight: '10px',
                }}
              >
                {t('showing_insights_for')} {selectedDate}
              </Typography>
              <Button
                sx={{
                  backgroundColor: palette.primary,
                  color: palette.textPrimary,
                  textTransform: 'none',
                  height: '40px',
                  paddingLeft: '15px',
                  paddingRight: '15px',
                  fontSize: '12px',
                  '&:hover': {
                    backgroundColor: enableDarkTheme
                      ? palette.boxBackground
                      : palette.boxBackground,
                  },
                }}
                onClick={() => {
                  setSelectedDate(null);
                }}
              >
                {t('reset')}
              </Button>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: ThemePalette.typography.grey,
                  marginLeft: '20px',
                  marginRight: '20px',
                }}
              >
                {t('show')}
              </Typography>
              <FormControl sx={{ minWidth: 120 }}>
                <Select
                  value={sortBy}
                  onChange={handleChange}
                  displayEmpty
                  IconComponent={KeyboardArrowDown}
                  sx={{
                    height: '40px',
                    width: '206px',
                    fontSize: '14px',
                    color: fontColor,
                    border: '1px solid #26364C',
                    '& .MuiSelect-select': {
                      paddingLeft: '12px',
                      paddingRight: '32px !important',
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#9ca3af',
                      right: '8px',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: enableDarkTheme
                          ? palette.boxBackground
                          : palette.boxBackground,
                        color: fontColor,
                      },
                    },
                  }}
                >
                  <MenuItem value="7" sx={{ fontSize: '14px' }}>
                    {t('last_7_days')}
                  </MenuItem>
                  <MenuItem value="14" sx={{ fontSize: '14px' }}>
                    {t('last_2_weeks')}
                  </MenuItem>
                  <MenuItem value="31" sx={{ fontSize: '14px' }}>
                    {t('last_1_month')}
                  </MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      </Box>
      <Box>
        <FlexBox
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: 'inherit',
            height: '450px',
            flexDirection: 'column',
            paddingTop: '20px',
            paddingBottom: '10px',
          }}
          id="day-wise-distribution"
        >
          <ResponsiveContainer width="100%" height="100%">
            {metricType === 'Count' ? (
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 0, left: -10, bottom: 20 }}
              >
                <CartesianGrid
                  stroke={palette.cartesianGrid}
                  strokeDasharray="2 2"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  tick={{ fill: ThemePalette.typography.grey, fontSize: 12 }}
                  tickFormatter={(value: string) => convertStdDateString(value)}
                />
                <YAxis
                  tickLine={false}
                  tick={{ fill: ThemePalette.typography.grey, fontSize: 12 }}
                  domain={[0, 80]}
                  ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80]}
                  label={{
                    value: t('count_axis'),
                    angle: -90,
                    position: 'inside',
                    fill: '#9CA3AF',
                    dy: 10,
                    dx: -12,
                  }}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  wrapperStyle={{ outline: 'none' }}
                />
                <Bar
                  dataKey="tireOk"
                  stackId="a"
                  fill="#58BF6A"
                  radius={[0, 0, 0, 0]}
                  barSize={62}
                  onClick={handleBarClick}
                  style={{ cursor: 'pointer' }}
                />
                <Bar
                  dataKey="tireWarning"
                  stackId="a"
                  fill="#EB5757"
                  radius={[4, 4, 0, 0]}
                  barSize={62}
                  onClick={handleBarClick}
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            ) : (
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 0, left: -10, bottom: 20 }}
              >
                <CartesianGrid
                  stroke={palette.cartesianGrid}
                  strokeDasharray="2 2"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  tick={{ fill: ThemePalette.typography.grey, fontSize: 12 }}
                  tickFormatter={(value: string) => convertStdDateString(value)}
                />
                <YAxis
                  tickLine={false}
                  tick={{ fill: ThemePalette.typography.grey, fontSize: 12 }}
                  domain={[0, 80]}
                  ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80]}
                  label={{
                    value: t('count_axis'),
                    angle: -90,
                    position: 'inside',
                    fill: '#9CA3AF',
                    dy: 10,
                    dx: -12,
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="tireOk"
                  stroke="#58BF6A"
                  strokeWidth={2}
                  name={t('tire_ok')}
                  activeDot={createActiveDot('tireOk')}
                />
                <Line
                  type="monotone"
                  dataKey="tireWarning"
                  stroke="#EB5757"
                  strokeWidth={2}
                  name={t('tire_warning')}
                  activeDot={createActiveDot('tireWarning')}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </FlexBox>
      </Box>
    </Box>
  );
};

export default DayWiseTrend;
